<template>
  <v-autocomplete
    :model-value="modelValue"
    :items="items"
    :loading="loading"
    :label="label"
    :prepend-inner-icon="icon"
    item-title="name"
    item-value="evaId"
    no-filter
    hide-details="auto"
    auto-select-first
    :no-data-text="noDataText"
    @update:search="onSearch"
    @update:model-value="onSelect"
  >
    <template #item="{ props: itemProps, item }">
      <v-list-item v-bind="itemProps" :subtitle="item.raw.monitored ? 'Historie verfügbar' : null">
        <template #prepend>
          <v-icon :icon="item.raw.monitored ? 'mdi-chart-line' : 'mdi-map-marker-outline'"
                  :color="item.raw.monitored ? 'primary' : undefined" size="small" />
        </template>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script>
// The server already filters, so `no-filter` keeps Vuetify from filtering the
// result set a second time and hiding hits whose name does not literally contain
// the typed string.
const DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 2;
const LIMIT = 10;

export default {
  name: 'StationField',

  props: {
    modelValue: { type: String, default: null },

    // The station `modelValue` stands for. Vuetify titles a model value it cannot find in
    // `items` with the raw value itself, so without this the field reads "8000105" in three
    // ordinary states — a shared link, a pair restored from the store, and (within one session)
    // after picking a station and then typing again, because the refetch replaces the list the
    // selection was titled from.
    station: { type: Object, default: null },

    label: { type: String, required: true },
    icon: { type: String, default: 'mdi-map-marker' },
  },

  emits: ['update:modelValue', 'update:station'],

  data() {
    return {
      results: [],
      selection: this.station,
      loading: false,
      query: '',
      timer: null,
    };
  },

  computed: {
    // The selected station always stays in the list, so no refetch can drop its title.
    // Appended rather than prepended: `auto-select-first` highlights the first entry, and
    // that has to remain the best hit for what the user just typed.
    items() {
      if (!this.selection || !this.selection.evaId
          || this.results.some(item => item.evaId === this.selection.evaId)) {
        return this.results;
      }
      return this.results.concat([this.selection]);
    },

    noDataText() {
      if (this.query.length < MIN_QUERY_LENGTH) {
        return 'Mindestens zwei Zeichen eingeben.';
      }
      return this.loading ? 'Suche läuft …' : 'Kein Bahnhof gefunden.';
    },
  },

  watch: {
    station: function(value) {
      this.selection = value;
    },
  },

  beforeUnmount() {
    if (this.timer) {
      clearTimeout(this.timer);
    }
  },

  methods: {
    // Debounced, because this fires on every keystroke and every request behind it
    // leaves this deployment for an unofficial upstream.
    onSearch: function(value) {
      this.query = value || '';
      if (this.timer) {
        clearTimeout(this.timer);
        this.timer = null;
      }
      // Vuetify emits this for a programmatic model change too (a restored pair, a shared
      // link) and again right after a pick, with the selected title. Looking "8000105" or
      // "Berlin Hbf" up when that is already what is selected spends an upstream request
      // on an answer we are holding.
      if (this.query === this.modelValue
          || (this.selection && this.query === this.selection.name)) {
        return;
      }
      if (this.query.length < MIN_QUERY_LENGTH) {
        this.results = [];
        return;
      }
      this.timer = setTimeout(this.fetch, DEBOUNCE_MS);
    },

    fetch: function() {
      var query = this.query;
      this.loading = true;
      this.$api.get(`/stations?q=${encodeURIComponent(query)}&limit=${LIMIT}`)
        .then(response => {
          // A slower earlier request must not overwrite a newer result.
          if (query === this.query) {
            this.results = response.data || [];
          }
        })
        .catch(e => {
          this.$log.warn(`Station lookup failed: ${e}`);
          this.results = [];
        })
        .finally(() => {
          this.loading = false;
        });
    },

    onSelect: function(evaId) {
      var station = this.items.find(item => item.evaId === evaId) || null;
      this.selection = station;
      this.$emit('update:modelValue', evaId);
      this.$emit('update:station', station);
    },
  },
};
</script>
